import React from 'react';
import {
  View,
  StyleSheet,
  Pressable,
  Modal,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { ThemedText, Caption } from '@/components/ui/Text';
import { Card } from '@/components/ui/Card';

export type MapStyle = 'streets' | 'satellite' | 'outdoors';

const MAP_STYLES: Array<{ id: MapStyle; name: string; icon: string; description: string }> = [
  {
    id: 'streets',
    name: 'Streets',
    icon: 'map-outline',
    description: 'Standard street map with labels',
  },
  {
    id: 'satellite',
    name: 'Satellite',
    icon: 'globe-outline',
    description: 'Satellite imagery',
  },
  {
    id: 'outdoors',
    name: 'Outdoors',
    icon: 'trail-sign-outline',
    description: 'Topographic map for outdoor activities',
  },
];

type MapStyleSelectorProps = {
  visible: boolean;
  currentStyle: MapStyle;
  onSelectStyle: (style: MapStyle) => void;
  onClose: () => void;
};

export const MapStyleSelector: React.FC<MapStyleSelectorProps> = ({
  visible,
  currentStyle,
  onSelectStyle,
  onClose,
}) => {
  const theme = useTheme();
  const { height: windowHeight } = useWindowDimensions();
  /** Leave room for overlay padding + header; scroll if options exceed this */
  const optionsMaxHeight = Math.min(windowHeight * 0.5, 420);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={[styles.overlay, { backgroundColor: theme.colors.modalOverlay }]}>
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Dismiss"
        />
        <View style={styles.sheetWrap} pointerEvents="box-none">
          <Card variant="outlined" style={[styles.modal, { backgroundColor: theme.colors.surface }]}>
            <View style={styles.header}>
              <ThemedText variant="h3" style={styles.title}>
                Map Style
              </ThemedText>
              <Pressable onPress={onClose} style={styles.closeButton}>
                <Ionicons name="close" size={24} color={theme.colors.text} />
              </Pressable>
            </View>

            <ScrollView
              style={[styles.optionsScroll, { maxHeight: optionsMaxHeight }]}
              contentContainerStyle={styles.options}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={false}
              bounces={false}
            >
              {MAP_STYLES.map((style) => {
                const isSelected = currentStyle === style.id;
                return (
                  <Pressable
                    key={style.id}
                    onPress={() => {
                      onSelectStyle(style.id);
                      onClose();
                    }}
                    style={({ pressed }) => [
                      styles.option,
                      {
                        backgroundColor: isSelected
                          ? theme.colors.primaryContainer
                          : theme.colors.background,
                        borderColor: isSelected
                          ? theme.colors.primary
                          : theme.colors.borderMuted,
                        opacity: pressed ? 0.7 : 1,
                      },
                    ]}
                  >
                    <View style={styles.optionContent}>
                      <View
                        style={[
                          styles.iconContainer,
                          {
                            backgroundColor: isSelected
                              ? theme.colors.primary + '15'
                              : theme.colors.background,
                          },
                        ]}
                      >
                        <Ionicons
                          name={style.icon as any}
                          size={24}
                          color={isSelected ? theme.colors.primary : theme.colors.textSecondary}
                        />
                      </View>
                      <View style={styles.optionText}>
                        <ThemedText
                          variant="bodyStrong"
                          style={[
                            styles.optionTitle,
                            { color: isSelected ? theme.colors.primary : theme.colors.text },
                          ]}
                        >
                          {style.name}
                        </ThemedText>
                        <Caption color={theme.colors.textSecondary} style={styles.optionDescription}>
                          {style.description}
                        </Caption>
                      </View>
                      {isSelected ? (
                        <View style={styles.checkWrap}>
                          <Ionicons name="checkmark-circle" size={24} color={theme.colors.primary} />
                        </View>
                      ) : (
                        <View style={styles.checkPlaceholder} />
                      )}
                    </View>
                  </Pressable>
                );
              })}
            </ScrollView>
          </Card>
        </View>
      </View>
    </Modal>
  );
};

// Export helper to get Mapbox style URL
export const getMapboxStyleURL = (style: MapStyle): string => {
  switch (style) {
    case 'satellite':
      return 'mapbox://styles/mapbox/satellite-streets-v12';
    case 'outdoors':
      return 'mapbox://styles/mapbox/outdoors-v12';
    case 'streets':
    default:
      return 'mapbox://styles/mapbox/streets-v12';
  }
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  sheetWrap: {
    width: '100%',
    maxWidth: 400,
  },
  modal: {
    width: '100%',
    borderRadius: 16,
    padding: 20,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  title: {
    flex: 1,
  },
  closeButton: {
    padding: 4,
  },
  optionsScroll: {
    flexGrow: 0,
  },
  options: {
    gap: 12,
    paddingBottom: 2,
  },
  option: {
    borderRadius: 12,
    borderWidth: 2,
    padding: 16,
  },
  optionContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
    marginTop: 2,
  },
  optionText: {
    flex: 1,
    minWidth: 0,
    flexShrink: 1,
    gap: 4,
  },
  optionTitle: {
    flexShrink: 1,
  },
  optionDescription: {
    fontSize: 12,
    flexShrink: 1,
  },
  checkWrap: {
    flexShrink: 0,
    marginTop: 2,
  },
  checkPlaceholder: {
    width: 24,
    height: 24,
    flexShrink: 0,
  },
});
